import { z } from "zod";

export const MAX_INVOICE_REFERENCES = 20;
export const MAX_INVOICE_REFERENCE_TEXT_LENGTH = 500;

/**
 * One invoice attached to a task-chat message.
 *
 * Snapshot fields are CLIENT-SUPPLIED AND UNVERIFIED - provided by the
 * browser at send time and NOT validated against server-side invoice data.
 * Never use them for business logic or financial computations.
 * Treat as a display-only hint that may be stale or fabricated.
 */
export const InvoiceReferenceSchema = z.object({
    /** Task the invoice list belongs to */
    taskId: z.string().uuid(),
    invoiceId: z.string().uuid(),
    /** Invoice number snapshot captured at send time */
    invoiceNumber: z
        .string()
        .max(MAX_INVOICE_REFERENCE_TEXT_LENGTH)
        .nullable()
        .optional(),
    /** Customer name snapshot captured at send time */
    customerName: z
        .string()
        .max(MAX_INVOICE_REFERENCE_TEXT_LENGTH)
        .nullable()
        .optional(),
    /** Human-readable chip label, generated client-side for display */
    label: z.string().min(1).max(MAX_INVOICE_REFERENCE_TEXT_LENGTH).optional(),
});
export type InvoiceReference = z.infer<typeof InvoiceReferenceSchema>;

export function buildInvoiceRefLabel(
    ref: Pick<InvoiceReference, "invoiceNumber" | "customerName">,
): string {
    const parts = [ref.invoiceNumber, ref.customerName].filter(
        (part): part is string => !!part,
    );
    return parts.length ? parts.join(" - ") : "Invoice";
}
